// System → AI Platform Keys — the shared keys that serve tenants granted the
// "Dana Managed AI Key" product (Tenant Details → Features & Products).
// Resolution: a tenant's own key always wins; these serve the rest.
import { fmtDate, fmtDateTime } from '../../lib/format';
import { useEffect, useState } from 'react';
import { Alert, AutoComplete, Button, Card, Descriptions, Form, Input, message, Select, Space, Tag, Typography } from 'antd';
import {
  getPlatformAiSettings, updatePlatformAiSettings, type PlatformAiSettings,
} from '../../services/platformAiService';
import { CLAUDE_MODELS, OPENAI_MODELS } from '../../services/aiConfigService';

export function AiPlatformKeysPage() {
  const [settings, setSettings] = useState<PlatformAiSettings | null>(null);
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [defaultProvider, setDefaultProvider] = useState<'anthropic' | 'openai'>('anthropic');
  const [defaultModel, setDefaultModel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () =>
    getPlatformAiSettings()
      .then((s) => {
        setSettings(s);
        setDefaultProvider(s.defaultProvider || 'anthropic');
        setDefaultModel(s.defaultModel || '');
      })
      .catch(() => message.error('Could not load platform AI settings'));

  useEffect(() => { void load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, string | null> = {};
      if (anthropicKey.trim()) body.anthropicKey = anthropicKey.trim();
      if (openaiKey.trim()) body.openaiKey = openaiKey.trim();
      body.defaultProvider = defaultProvider;
      body.defaultModel = defaultModel.trim() || null;
      const next = await updatePlatformAiSettings(body);
      setSettings(next);
      setAnthropicKey('');
      setOpenaiKey('');
      message.success('Platform AI keys saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 760 }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>AI Platform Keys</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Shared provider keys that serve tenants granted <Tag>Dana Managed AI Key</Tag>
          in their Features &amp; Products. A tenant&apos;s own key always takes precedence.
          Keys are stored encrypted and never displayed again after saving.
        </Typography.Paragraph>
      </div>

      <Alert
        type="warning"
        showIcon
        message="Platform cost control"
        description="Every tenant you grant Dana Managed AI Key bills its usage to these keys. Grant deliberately, per tenant, from the tenant's Features panel — there is no global default-on."
      />

      <Card title="Current status" loading={!settings}>
        {settings && (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Default provider (Dana reasoning)">
              <Tag color="blue">{(settings.defaultProvider || 'anthropic') === 'openai' ? 'OpenAI' : 'Anthropic (Claude)'}</Tag>
              {' '}
              <Typography.Text type="secondary">
                — used by managed tenants without their own choice
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Anthropic key">
              {settings.anthropicKeySet
                ? <Tag color="green">set · …{settings.anthropicKeyLast4}</Tag>
                : <Tag>not set</Tag>}
              {' '}
              <Typography.Text type="secondary">reasoning when provider = Anthropic</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="OpenAI key">
              {settings.openaiKeySet
                ? <Tag color="green">set · …{settings.openaiKeyLast4}</Tag>
                : <Tag>not set</Tag>}
              {' '}
              <Typography.Text type="secondary">reasoning when provider = OpenAI, and voice transcription</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Default model">
              {settings.defaultModel || <Typography.Text type="secondary">provider default (e.g. gpt-4o-mini)</Typography.Text>}
            </Descriptions.Item>
            {settings.updatedAt && (
              <Descriptions.Item label="Last updated">{fmtDateTime(settings.updatedAt)}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Card>

      <Card title="Update default & keys">
        <Form layout="vertical" onFinish={save}>
          <Form.Item
            label="Default reasoning provider"
            help="Which shared key managed tenants use for Dana chat when they haven't chosen their own. Set OpenAI to make gpt-4o-mini (etc.) the platform default."
          >
            <Select
              value={defaultProvider}
              onChange={(v) => setDefaultProvider(v)}
              style={{ maxWidth: 320 }}
              options={[
                { value: 'anthropic', label: 'Anthropic (Claude)' },
                { value: 'openai', label: 'OpenAI (GPT)' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Anthropic API key (sk-ant-…)" extra="Used for reasoning when the default provider is Anthropic. Leave blank to keep the current key.">
            <Input.Password value={anthropicKey} onChange={(e) => setAnthropicKey(e.target.value)} placeholder="sk-ant-…" autoComplete="off" />
          </Form.Item>
          <Form.Item label="OpenAI API key (sk-…)" extra="Used for reasoning when the default provider is OpenAI, and for voice transcription. Leave blank to keep the current key.">
            <Input.Password value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} placeholder="sk-…" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Default model for managed tenants (optional)"
            help="Any model id works — type your own; new models take effect immediately. Suggestions below. Blank = the provider's default (e.g. gpt-4o-mini)."
          >
            <AutoComplete
              value={defaultModel}
              onChange={setDefaultModel}
              options={[
                { label: 'Anthropic (Claude)', options: CLAUDE_MODELS },
                { label: 'OpenAI (GPT)', options: OPENAI_MODELS },
              ]}
              placeholder="e.g. claude-sonnet-4-6"
              filterOption={(input, opt) =>
                String((opt as { value?: string })?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
        </Form>
      </Card>
    </Space>
  );
}
